import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattingToolbar } from '@/components/FormattingToolbar';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  className?: string;
  'data-testid'?: string;
  minHeight?: string;
  maxHeight?: string;
  expandable?: boolean;
  /** When true, editor fills parent height; toolbar stays fixed, only content scrolls */
  fillHeight?: boolean;
  editorRef?: React.MutableRefObject<any>;
}

function convertPlainTextToHtml(text: string): string {
  if (!text) return '';
  if (text.trim().startsWith('<') && (text.includes('<p>') || text.includes('<h1>') || text.includes('<h2>') || text.includes('<ul>') || text.includes('<ol>'))) {
    return text;
  }
  const paragraphs = text.split(/\n\n+/);
  return paragraphs
    .map(para => {
      const lines = para.split(/\n/);
      return `<p>${lines.join('<br>')}</p>`;
    })
    .join('');
}

export function RichTextEditor({ content, onChange, className, 'data-testid': testId, minHeight = '400px', maxHeight = '600px', expandable = false, fillHeight = false, editorRef }: RichTextEditorProps) {
  const processedContent = useMemo(() => convertPlainTextToHtml(content), [content]);
  const [isToolbarExpanded, setIsToolbarExpanded] = useState(false);
  const editorHeightClass = useMemo(
    () =>
      expandable
        ? `prose prose-sm dark:prose-invert max-w-none focus:outline-none p-4 text-sm [&>p]:mb-0 [&>p]:leading-normal [&>ul]:mb-2 [&>ol]:mb-2 [&_li]:mb-0 [&_li]:mt-0 [&_li]:leading-normal [&_li>p]:mt-0 [&_li>p]:mb-0 [&_hr]:my-1 [&>p+ol]:-mt-1 [&>p+ul]:-mt-1`
        : `prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[${minHeight}] p-4 text-sm [&>p]:mb-0 [&>p]:leading-normal [&>ul]:mb-2 [&>ol]:mb-2 [&_li]:mb-0 [&_li]:mt-0 [&_li]:leading-normal [&_li>p]:mt-0 [&_li>p]:mb-0 [&_hr]:my-1 [&>p+ol]:-mt-1 [&>p+ul]:-mt-1`,
    [expandable, minHeight]
  );

  const editorExtensions = useMemo(
    () => [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Highlight.configure({
        multicolor: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'source-link',
        },
      }),
    ],
    []
  );

  const editorProps = useMemo(
    () => ({
      attributes: {
        class: editorHeightClass,
        style: expandable ? `min-height: ${minHeight}` : `min-height: ${minHeight}`,
      },
      handleClick: (_view: any, _pos: number, event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
        if (anchor) {
          event.preventDefault();
          event.stopPropagation();
          window.open(anchor.href, '_blank', 'noopener,noreferrer');
          return true;
        }
        return false;
      },
    }),
    [editorHeightClass, expandable, minHeight]
  );

  const handleUpdate = useCallback(
    ({ editor }: { editor: { getHTML: () => string } }) => {
      onChange(editor.getHTML());
    },
    [onChange]
  );
  
  const editor = useEditor({
    extensions: editorExtensions,
    content: processedContent || '',
    editorProps,
    onUpdate: handleUpdate,
  });

  const handleToggleToolbar = useCallback(() => {
    setIsToolbarExpanded((prev) => !prev);
  }, []);

  const handleToolbarExpandedChange = useCallback((open: boolean) => {
    setIsToolbarExpanded(open);
  }, []);

  useEffect(() => {
    if (editorRef && editor) {
      editorRef.current = editor;
    }
  }, [editor, editorRef]);

  useEffect(() => {
    if (editor) {
      if (processedContent !== editor.getHTML()) {
        editor.commands.setContent(processedContent || '');
      }
    }
  }, [processedContent, editor]);



  if (!editor) {
    return null;
  }

  return (
    <div className={`border rounded-md bg-background flex flex-col ${fillHeight ? 'h-full min-h-0' : ''} ${className || ''}`} data-testid={testId || "rich-text-editor"}>
      <style>{`
        [data-testid="rich-text-editor"] .ProseMirror p,
        [data-testid="rich-text-editor"] .ProseMirror > p,
        .ProseMirror p {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }
        [data-testid="rich-text-editor"] .ProseMirror p + p,
        .ProseMirror p + p {
          margin-top: 0 !important;
        }
        .ProseMirror a,
        .ProseMirror a.source-link {
          color: hsl(var(--primary, 221 83% 53%));
          text-decoration: underline;
          text-underline-offset: 2px;
          cursor: pointer !important;
          transition: color 0.15s ease;
        }
        .ProseMirror a:hover,
        .ProseMirror a.source-link:hover {
          color: hsl(var(--primary, 221 83% 53%) / 0.8);
          text-decoration-thickness: 2px;
        }
      `}</style>
      <FormattingToolbar
        editor={editor}
        isToolbarExpanded={isToolbarExpanded}
        onToggleToolbar={handleToggleToolbar}
        onToolbarExpandedChange={handleToolbarExpandedChange}
      />

      {/* Only this EditorContent wrapper scrolls when text overflows; toolbar and the rest of RichTextEditor stay fixed */}
      <div
        className={fillHeight ? "flex-1 min-h-0 overflow-y-auto" : "overflow-y-auto"}
        style={fillHeight ? undefined : { maxHeight }}
      >
        <EditorContent editor={editor} data-testid="editor-content" />
      </div>
    </div>
  );
}
